import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCredits } from '../hooks';
import {
  CreditBalanceCard,
  CreditPurchaseCard,
  SubscriptionCard,
  SpendingSummary,
  TransactionHistory,
  PaymentHistoryTable,
} from '../components';

export function CreditsPage() {
  const {
    balance,
    spendingSummary,
    transactions,
    transactionsTotal,
    loading,
    loadingTransactions,
    error,
    refresh,
    loadMoreTransactions,
    hasMoreTransactions,
  } = useCredits();

  const [showTransactions, setShowTransactions] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'payments'>('overview');
  const [searchParams] = useSearchParams();

  // Show success/cancel message from Stripe redirect
  const paymentStatus = searchParams.get('payment');
  const subscriptionStatus = searchParams.get('subscription');

  return (
    <div className="w-full max-w-4xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-foreground">Credits</h1>
        <button
          onClick={refresh}
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50"
        >
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {paymentStatus === 'success' && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 rounded-lg text-green-800 dark:text-green-200 text-sm">
          Payment successful! Your credits have been added to your account.
        </div>
      )}

      {paymentStatus === 'canceled' && (
        <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800/50 rounded-lg text-yellow-800 dark:text-yellow-200 text-sm">
          Payment was canceled. No credits were charged.
        </div>
      )}

      {subscriptionStatus === 'success' && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 rounded-lg text-green-800 dark:text-green-200 text-sm">
          Welcome to Premium! Your monthly credits have been added.
        </div>
      )}

      {subscriptionStatus === 'canceled' && (
        <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800/50 rounded-lg text-yellow-800 dark:text-yellow-200 text-sm">
          Subscription signup was canceled.
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive">
          {error}
        </div>
      )}

      {/* Tab navigation */}
      <div className="flex gap-1 mb-6 border-b border-border">
        <button
          onClick={() => setActiveTab('overview')}
          className={`w-40 py-2 text-sm font-medium text-center border-b-2 transition-colors ${
            activeTab === 'overview'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          Overview
        </button>
        <button
          onClick={() => setActiveTab('payments')}
          className={`w-40 py-2 text-sm font-medium text-center border-b-2 transition-colors ${
            activeTab === 'payments'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          Payment History
        </button>
      </div>

      {/* Fixed-width content area prevents layout shift between tabs */}
      <div className="w-full">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <CreditPurchaseCard />

            <SubscriptionCard />

            <CreditBalanceCard balance={balance} loading={loading} />

            <SpendingSummary categories={spendingSummary} loading={loading} />

            <div>
              <button
                onClick={() => setShowTransactions((v) => !v)}
                className="text-sm font-medium text-primary hover:text-primary/80 mb-4"
              >
                {showTransactions
                  ? 'Hide transactions'
                  : `Show all transactions (${transactionsTotal})`}
              </button>

              {showTransactions && (
                <TransactionHistory
                  transactions={transactions}
                  total={transactionsTotal}
                  loading={loading}
                  loadingMore={loadingTransactions}
                  hasMore={hasMoreTransactions}
                  onLoadMore={loadMoreTransactions}
                />
              )}
            </div>
          </div>
        )}

        {activeTab === 'payments' && (
          <div className="space-y-6">
            <PaymentHistoryTable />
          </div>
        )}
      </div>
    </div>
  );
}
